import * as Sentry from '@sentry/browser';
import { loader } from 'graphql.macro';
import flatten from 'lodash/flatten';
import moment from 'moment';
import momentTimezone from 'moment-timezone';
import { omit } from 'lodash';

import { appClient } from 'src/config/apollo';
import { config } from 'src/config/config';
import { RootState } from 'src/config/rootStore';
import {
    CrisisProviderCompanyAssignmentNode,
    CrisisProviderCountryAssignmentNode,
    MemberInsuranceNode,
} from 'src/generated/webapp_gql';
import { SessionUtilizationInfo } from 'src/modules/videoSessions/models/videoSessions';
import { capture } from 'src/shared/helpers/sentryCapture';
import { Http } from 'src/shared/services/Http';
import { ToastNotificationsService } from 'src/shared/services/ToastNotificationsService';

import { GetCrisisProviderCompanyAssignmentsQueryDocument } from '../documents/getCrisisProviderCompanyAssignmentsQuery.generated';
import { GetCrisisProviderCountryAssignmentsQueryDocument } from '../documents/getCrisisProviderCountryAssignmentsQuery.generated';
import { GetHpiEligibilityStatusQueryDocument } from '../documents/getHPIEligibilityStatusQuery.generated';
import { GetHpiMemberInsuranceQueryDocument } from '../documents/getHPIMemberInsuranceQuery.generated';
import {
    ChangePasswordPayload,
    CrisisEvent,
    UpdateUserInfoPayload,
    UserDetails,
} from '../models/user';
import { getUserInformation } from '../selectors/userSelectors';

// const breakCache = 0
const GET_MEMBER_INFORMATION_QUERY = loader('../documents/getMemberInformationQuery.graphql');

export class User {
    constructor(private http: Http, private toastNotificationsService: ToastNotificationsService) {}

    public async fetchUserInfo(): Promise<{
        userDetails: UserDetails;
        sessionInfo: SessionUtilizationInfo;
    }> {
        try {
            const { data } = await appClient.query({
                query: GET_MEMBER_INFORMATION_QUERY,
                fetchPolicy: 'network-only',
            });

            const { company, visitsRemaining, careRecommendations, notificationPreferences } =
                data?.me;

            const userDetails: UserDetails = {
                ...data.me,
                company: company.name,
                companyUuid: company.uuid,
                companyHrEmail: company.hrEmail,
                companySsoOnly: company.ssoOnly,
                careRecommendations: careRecommendations.sort((a, b) => {
                    return +new Date(b.startedAt) - +new Date(a.startedAt);
                }),
                notificationPreferences: omit(notificationPreferences, '__typename'),
            };

            const sessionInfo: SessionUtilizationInfo = {
                coveredCoachingVisits: company.coveredCoachingVisits,
                coveredTherapyVisits: company.coveredTherapyVisits,
                coachingVisitsRemaining: visitsRemaining.coachingVisitsRemaining,
                therapyVisitsRemaining: visitsRemaining.therapyVisitsRemaining,
                resetDate: moment(company.resetDate),
            };

            userDetails.localTimezone = this.overwriteTimezone(
                userDetails.localTimezone,
                userDetails.id,
            );

            return {
                userDetails,
                sessionInfo,
            };
        } catch (error) {
            return Promise.reject(error);
        }
    }

    public async changePassword(data: ChangePasswordPayload): Promise<void> {
        // has to be done this way because of conflict with legacy reducers in unit tests
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const { store } = require('src/config/rootStore');

        const { id } = getUserInformation(store.getState() as RootState);

        try {
            await this.updateUserInfo(
                { password: data.newPassword, oldPassword: data.currentPassword },
                id,
            );
        } catch {
            return Promise.reject(config.messages.currentPasswordIncorrect);
        }

        this.toastNotificationsService.renderSuccessToast(config.messages.passwordChangedSuccess);
    }

    public async updateEmailAddress(email: string, userId: number): Promise<UserDetails> {
        try {
            const updatedInfo = await this.http.PATCH<Partial<UserDetails>, UserDetails>(
                `accounts/${userId}/`,
                { email },
            );

            return updatedInfo;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            let errorMessage = '';

            if (error.body?.email?.length) {
                errorMessage = error.body.email[0];
            } else {
                Sentry.captureException(error);

                this.toastNotificationsService.renderErrorToast(config.messages.genericError);
            }

            return Promise.reject(errorMessage);
        }
    }

    public async updateUserInfo(
        userDetails: Partial<UpdateUserInfoPayload>,
        userId: number,
    ): Promise<UserDetails | void> {
        try {
            const updatedInfo = await this.http.PATCH<Partial<UpdateUserInfoPayload>, UserDetails>(
                `accounts/${userId}/`,
                userDetails,
            );

            return updatedInfo;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            if (error.body) {
                const errors: string[] = flatten(Object.values(error.body));

                errors.forEach(this.toastNotificationsService.renderErrorToast);
            } else {
                this.toastNotificationsService.renderErrorToast(config.messages.genericError);
            }

            return Promise.reject();
        }
    }

    public async fetchCrisisProvidersAssignedToCompany(): Promise<
        CrisisProviderCompanyAssignmentNode[]
    > {
        const { data } = await appClient.query({
            query: GetCrisisProviderCompanyAssignmentsQueryDocument,
            fetchPolicy: 'network-only',
        });

        return data?.crisisProviderCompanyAssignments;
    }

    public async fetchCrisisProvidersAssignedToCountry(): Promise<
        CrisisProviderCountryAssignmentNode[]
    > {
        const { data } = await appClient.query({
            query: GetCrisisProviderCountryAssignmentsQueryDocument,
            fetchPolicy: 'network-only',
        });

        return data?.crisisProviderCountryAssignments;
    }

    public async fetchHPIEligibilityStatus(): Promise<boolean> {
        const { data } = await appClient.query({
            query: GetHpiEligibilityStatusQueryDocument,
            fetchPolicy: 'network-only',
        });

        return data?.getMyEligibilityStatus;
    }

    public async fetchHPIMemberInsurance(): Promise<MemberInsuranceNode | null> {
        const { data } = await appClient.query({
            query: GetHpiMemberInsuranceQueryDocument,
            fetchPolicy: 'network-only',
        });

        return data?.getMemberInsurance;
    }

    private overwriteTimezone(currentTimezone: string, accountId: number): string {
        const timezoneGuess = momentTimezone.tz.guess();
        const timezoneGuessValid = typeof timezoneGuess === 'string' && timezoneGuess !== '';

        if (timezoneGuessValid && currentTimezone !== timezoneGuess) {
            this.updateUserInfo({ localTimezone: timezoneGuess }, accountId);
        }

        return timezoneGuessValid ? timezoneGuess : currentTimezone;
    }

    public async submitHelpDeskRequest(message: string, tags: string[] = []): Promise<void> {
        try {
            await this.http.POST('connect-to-expert/help_desk_submission/', {
                content: message,
                tags,
            });
        } catch (e) {
            this.toastNotificationsService.renderErrorToast(config.messages.genericError);
            capture(e);

            return Promise.reject();
        }
    }

    public createCrisisEventRequest(source: string): CrisisEvent {
        const request: CrisisEvent = {
            platform: 'web', // Match with django-backend DeviceCategory enum
            source: source,
        };
        return request;
    }

    public async submitCrisisResourceEvent(source: string): Promise<void> {
        const request = this.createCrisisEventRequest(source);
        try {
            await this.http.POST('connect-to-expert/crisis_resource_event/', request);
        } catch (e) {
            this.toastNotificationsService.renderErrorToast(config.messages.genericError);

            return Promise.reject();
        }
    }
}
